/**
 * Service Worker Update Utility
 * Forces service worker to update and clear caches to ensure latest Header/menu loads
 * Also handles recovery from stale chunk 404 errors
 */

import { isE2EMode } from "@/lib/e2eMode";

/** Cache-bust marker after forced SW/cache clear — never leave it in the address bar. */
export const O9_REFRESH_QUERY = "__o9_refreshed";

/**
 * Remove `?__o9_refreshed=…` from the URL without reloading (clean www.opus9.com).
 * Safe to call on every boot.
 */
export function stripRefreshMarkerFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(O9_REFRESH_QUERY)) return;
    url.searchParams.delete(O9_REFRESH_QUERY);
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
    window.history.replaceState(window.history.state, "", next || "/");
  } catch {
    /* ignore */
  }
}

/**
 * Detect if there are any failed chunk loads (404 on JS assets)
 * This happens when the SW has cached references to old chunk hashes
 */
export const detectStaleChunks = (): boolean => {
  // Check if we're already in recovery mode to prevent loops
  const recoveryKey = 'o9_chunk_recovery';
  const lastRecovery = sessionStorage.getItem(recoveryKey);
  if (lastRecovery) {
    const elapsed = Date.now() - parseInt(lastRecovery, 10);
    // If we recovered less than 30 moments ago, don't trigger again
    if (elapsed < 30000) return false;
  }
  return true;
};

/**
 * Handle chunk load failures by clearing all caches and reloading
 */
export const handleChunkLoadError = async (): Promise<void> => {
  if (isE2EMode()) {
    console.warn("[SW] Chunk load recovery skipped (E2E mode)");
    return;
  }
  // Prevent recovery loops
  const recoveryKey = 'o9_chunk_recovery';
  sessionStorage.setItem(recoveryKey, Date.now().toString());

  console.warn('[SW] Detected stale chunk, clearing caches and reloading...');

  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Hard reload to get fresh assets
    window.location.reload();
  } catch (error) {
    console.error('[SW] Recovery failed:', error);
    window.location.reload();
  }
};

export const forceServiceWorkerUpdate = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations();

    // Unregister all service workers to force fresh start
    await Promise.all(
      registrations.map((registration) => registration.unregister())
    );

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName))
      );
    }

    // Reload the page to get fresh service worker
    window.location.reload();
  } catch (error) {
    console.error('Error updating service worker:', error);
    // If update fails, at least try to reload
    window.location.reload();
  }
};

/**
 * Check if service worker needs update and prompt user
 */
export const checkServiceWorkerUpdate = (): void => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      // Check for updates
      registration.update();

      // Listen for new service worker
      registration.addEventListener('updatefound', () => {
        // New service worker available - force reload
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        }
      });
    });
  });
};

/**
 * Initialize service worker update checks
 */
export const initServiceWorkerUpdates = (): void => {
  // Safety guard: only run if service workers are supported
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    // Check for updates on page load
    checkServiceWorkerUpdate();

    // Check for updates every 5 minutes
    setInterval(checkServiceWorkerUpdate, 5 * 60 * 1000);

    // Listen for service worker controller changes
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // New service worker activated - reload to get latest code
      window.location.reload();
    });
  } catch (error) {
    // Silently fail - don't break the app if service worker updates fail
    if (import.meta.env.DEV) {
      console.warn('Service worker update initialization failed:', error);
    }
  }
};

/**
 * Force clear all caches and reload - use when UI appears stale
 * This is more aggressive than forceServiceWorkerUpdate
 */
export const forceCacheClearAndReload = async (): Promise<void> => {
  if (isE2EMode()) {
    console.warn("[Cache] Cache clear reload skipped (E2E mode)");
    return;
  }
  console.warn('[Cache] Forcing complete cache clear and reload...');

  try {
    // 1. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => {
        console.log(`[Cache] Unregistering SW: ${r.active?.scriptURL}`);
        return r.unregister();
      }));
    }

    // 2. Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => {
        console.log(`[Cache] Deleting cache: ${name}`);
        return caches.delete(name);
      }));
    }

    // 3. Clear storage markers that might block recovery
    try {
      sessionStorage.removeItem('o9_chunk_recovery');
      sessionStorage.removeItem('o9_boot_recovery_v1');
      sessionStorage.removeItem('o9_boot_mounted_v1');
    } catch { /* ignore */ }

    // 4. Cache-bust reload — timestamp query bypasses SW/HTTP caches once.
    // After load, stripRefreshMarkerFromUrl() cleans the address bar.
    console.log('[Cache] Reloading page...');
    const next = new URL(window.location.href);
    next.searchParams.set(O9_REFRESH_QUERY, String(Date.now()));
    window.location.replace(next.toString());
  } catch (error) {
    console.error('[Cache] Clear failed:', error);
    window.location.reload();
  }
};

/**
 * Global error handler for chunk load failures
 * Attach this to window.onerror to catch rapidly on dynamic imports
 */
export const installChunkErrorHandler = (): void => {
  const originalOnError = window.onerror;

  window.onerror = (message, source, lineno, colno, error) => {
    // Check if this is a chunk load failure (multiple variants)
    const isChunkError = typeof message === 'string' && (
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('error loading dynamically imported module')
    );
    if (isChunkError && detectStaleChunks()) {
      handleChunkLoadError();
      return true; // Prevent default error handling
    }

    // Call original handler if present
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Also catch unhandled promise rejections for dynamic imports
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason?.message ?? (typeof reason === 'string' ? reason : '');
    const isChunkRejection = typeof msg === 'string' && (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module')
    );
    if (reason && isChunkRejection && detectStaleChunks()) {
      event.preventDefault();
      event.stopPropagation();
      handleChunkLoadError();
    }
  });
};
